import { AxiosResponse } from 'axios';
import { API } from '../../utils/axios';

export type PlaceAutocompleteResult = {
  place_id: string;
  description: string;
  main_text: string;
  secondary_text: string;
};

export type PlaceDetails = {
  place_id: string;
  formatted_address: string;
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
};

export type PlacesAutocompleteRequest = {
  input: string;
  language?: string;
  components?: string;
  place_id?: string; // For getting place details
  patient_uuid?: string;
};

class PlacesService {
  /**
   * Get place autocomplete suggestions
   */
  static async autocomplete(params: PlacesAutocompleteRequest): Promise<PlaceAutocompleteResult[]> {
    try {
      if (!params.input || params.input.trim().length < 2) {
        return [];
      }

      const response = await API.post<any, AxiosResponse<{ resource: PlaceAutocompleteResult[] }>>(
        `/v1/resources/Places/_autocomplete`,
        {
          resource: {
            input: params.input,
            language: params.language || 'en',
            ...(params.components && { components: params.components }),
            ...(params.patient_uuid && { patient_uuid: params.patient_uuid }),
          },
        },
        {
          headers: {
            'D2md-Client-App': 'web',
          },
        },
      );

      return response.data.resource || [];
    } catch (error) {
      console.error('Error searching places:', error);
      throw error;
    }
  }

  /**
   * Get place details by place_id
   */
  static async getPlaceDetails(
    placeId: string,
    patientUUID?: string,
  ): Promise<PlaceDetails | null> {
    try {
      const response = await API.post<any, AxiosResponse<{ resource: PlaceDetails }>>(
        `/v1/resources/Places/_autocomplete`,
        {
          resource: {
            place_id: placeId,
            ...(patientUUID && { patient_uuid: patientUUID }),
          },
        },
        {
          headers: {
            'D2md-Client-App': 'web',
          },
        },
      );

      return response.data.resource || null;
    } catch (error) {
      console.error('Error getting place details:', error);
      throw error;
    }
  }
}

export default PlacesService;
